import { create } from "zustand";
import { persist } from "zustand/middleware";

const SESSION_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

interface ClinicPatient {
  patient_id?: string | number;
  name?: string;
  age?: string | number;
  gender?: string;
  mobile?: string;
  phone?: string;
}

interface ClinicVisit {
  visit_id?: string | number;
  status?: string;
  visit_date?: string;
}

interface ClinicStore {
  activePatientId: string | null;
  activeVisitId: string | null;
  activePatient: ClinicPatient | null;
  activeVisit: ClinicVisit | null;
  selectedTooth: string | null;
  activeProcedureId: string | null;
  sessionDiagnoses: import("../types/domain").Diagnosis[];
  lastUpdated: number | null;

  setActivePatient: (_id: string | null) => void;
  setActiveVisit: (_id: string | null) => void;
  setActivePatientProfile: (_patient: ClinicPatient | null) => void;
  setActiveVisitRecord: (_visit: ClinicVisit | null) => void;
  setSelectedTooth: (_tooth: string | null) => void;
  setActiveProcedure: (_id: string | null) => void;
  setSessionDiagnoses: (_diagnoses: import("../types/domain").Diagnosis[]) => void;

  resetSession: () => void;
}

export const useClinicStore = create<ClinicStore>()(
  persist(
    (set) => ({
      activePatientId: null,
      activeVisitId: null,
      activePatient: null,
      activeVisit: null,
      selectedTooth: null,
      activeProcedureId: null,
      sessionDiagnoses: [],
      lastUpdated: null,

      setActivePatient: (id) =>
        set({ activePatientId: id, lastUpdated: Date.now() }),

      setActiveVisit: (id) =>
        set({ activeVisitId: id, lastUpdated: Date.now() }),

      setActivePatientProfile: (patient) =>
        set({
          activePatient: patient,
          activePatientId: patient?.patient_id != null ? String(patient.patient_id) : null,
          lastUpdated: Date.now(),
        }),

      setActiveVisitRecord: (visit) =>
        set({
          activeVisit: visit,
          activeVisitId: visit?.visit_id != null ? String(visit.visit_id) : null,
          lastUpdated: Date.now(),
        }),

      setSelectedTooth: (tooth) =>
        set({ selectedTooth: tooth, lastUpdated: Date.now() }),

      setActiveProcedure: (id) =>
        set({ activeProcedureId: id, lastUpdated: Date.now() }),

      setSessionDiagnoses: (diagnoses) =>
        set({ sessionDiagnoses: diagnoses, lastUpdated: Date.now() }),

      resetSession: () =>
        set({
          activePatientId: null,
          activeVisitId: null,
          activePatient: null,
          activeVisit: null,
          selectedTooth: null,
          activeProcedureId: null,
          sessionDiagnoses: [],
          lastUpdated: null,
        }),
    }),
    {
      name: "clinic-context",
      version: 1,
      partialize: (state) => ({
        activePatientId: state.activePatientId,
        activeVisitId: state.activeVisitId,
        activePatient: state.activePatient,
        activeVisit: state.activeVisit,
        lastUpdated: state.lastUpdated,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const ts = state.lastUpdated;
        if (ts && Date.now() - ts > SESSION_TTL_MS) {
          state.resetSession();
        }
      },
    }
  )
);
